// AgencyLoginPage.js - Login page for agency agents with 2FA
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import SecurityLockoutMessage from './SecurityLockoutMessage';
import AuthShell, { AuthField, AuthErrorBanner, AuthPrimaryButton, AuthPasswordInput, AUTH_INPUT } from './AuthShell';

const AgencyLoginPage = ({ agency, agencyCode }) => {
  const navigate = useNavigate();
  const { setUser, checkAuth, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA states
  const [show2FA, setShow2FA] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [userContact, setUserContact] = useState({ email: '', phone: '' });
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Step 1: Initial login attempt
        const response = await authService.login({
          email: formData.email,
          password: formData.password,
          agencyCode: agencyCode
        });
        
        // If 2FA is required, the backend will return a temp token
        if (response.data.data.requiresTwoFactor) {
          setTempToken(response.data.data.tempToken);
          setUserContact({
            email: response.data.data.maskedEmail || formData.email,
            phone: response.data.data.maskedPhone || ''
          });
          setShow2FA(true);
          setLoading(false);
          
          // Don't send initial 2FA code automatically - let user choose method first
        } else {
          // No 2FA required, proceed with login
          await handleLoginSuccess(response.data.data);
        }
      } else {
        // Redirect to agency registration
        navigate(`/${agencyCode.toLowerCase()}/register`);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      // Handle security lockout responses
      if (errorData?.type === 'account_locked' || errorData?.type === 'ip_rate_limited') {
        setError(errorData);
      } else if (errorData?.remainingAttempts !== undefined) {
        // Show failed attempts warning
        setError({
          ...errorData,
          type: 'failed_attempts_warning'
        });
      } else {
        setError(errorData?.message || 'Invalid credentials');
      }
      setLoading(false);
    }
  };

  const send2FACode = async (selectedMethod = verificationMethod) => {
    setSendingCode(true);
    setError('');
    try {
      await authService.send2FACode({
        tempToken,
        method: selectedMethod
      });
      setCodeSent(true);
      startResendTimer();
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verify2FA({
        tempToken,
        code: verificationCode
      });

      // Set user directly from 2FA response instead of calling checkAuth
      const userData = response.data.user;
      console.log('🔍 2FA Success - User data received:', userData);
      
      // Clear any existing auth state to prevent cache issues
      localStorage.removeItem('user');
      
      // Set user in context
      setUser(userData);
      console.log('🔍 2FA Success - User set in context');
      
      // Force a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      // DEBUG: Log user data to see what we're working with
      console.log('🔍 2FA Success - userData.role:', userData.role);
      console.log('🔍 2FA Success - userData.status:', userData.status);
      console.log('🔍 2FA Success - Full userData:', JSON.stringify(userData));

      // Check for commission-only agents first
      if (userData.role === 'agent' && userData.status === 'commission_only') {
        console.log('🔍 2FA Success - Commission-only agent detected, redirecting to commissions-only page');
        navigate('/agent/commissions-only', { replace: true });
        return;
      }

      // Navigate based on user role with cache busting
      const targetPath = userData.role === 'agency_admin' ? '/agency/dashboard' :
                        userData.role === 'agent' ? '/agent/dashboard' :
                        userData.role === 'super_admin' ? '/admin/dashboard' :
                        '/agent/dashboard';
      
      console.log('🔍 2FA Success - Navigating to:', targetPath);
      navigate(targetPath, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async (data) => {
    try {
      console.log('Login success data received:', data);
      
      // Store auth data properly
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        console.log('Token and user data stored successfully');
      } else {
        console.error('No token in response data:', data);
        throw new Error('No authentication token received');
      }
      
      // Use checkAuth to properly set the user state and ensure synchronization
      await checkAuth();
      
      // Wait a moment for React state to update, then use the user from context
      setTimeout(() => {
        // Access user from the context which has been updated by checkAuth
        const currentUser = user || data.user;
        
        console.log('Navigating based on user role:', currentUser.role, 'status:', currentUser.status);
        
        // Check for commission-only agents first
        if (currentUser.role === 'agent' && currentUser.status === 'commission_only') {
          console.log('Commission-only agent detected, redirecting to commissions-only page');
          navigate('/agent/commissions-only');
          return;
        }
        
        // Navigate based on user role
        if (currentUser.role === 'agency_admin') {
          navigate('/agency/dashboard');
        } else if (currentUser.role === 'agent') {
          navigate('/agent/dashboard');
        } else if (currentUser.role === 'super_admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/agent/dashboard'); // Default to agent dashboard
        }
      }, 200);
    } catch (error) {
      console.error('Error in handleLoginSuccess:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };

  const resend2FA = async () => {
    if (resendTimer > 0 || sendingCode) return;
    
    try {
      await send2FACode();
      setError('');
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    }
  };

  if (show2FA) {
    return (
      <AuthShell>
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
            Two-Factor Authentication
          </h2>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">
            {!codeSent ?
              'Select a delivery method below to send your verification code:' :
              sendingCode ?
                'Sending verification code...' :
                `Verification code sent to your ${verificationMethod === 'email' ? 'email' : 'phone'}`
            }
          </p>
        </div>

        <form onSubmit={handleVerify2FA} className="space-y-4">
          <AuthErrorBanner>{typeof error === 'string' ? error : null}</AuthErrorBanner>

          {!codeSent ? (
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)]">Choose your preferred delivery method</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={async () => { setVerificationMethod('email'); await send2FACode('email'); }}
                  disabled={sendingCode}
                  className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold border transition-all disabled:opacity-50"
                  style={verificationMethod === 'email' && sendingCode
                    ? { borderColor: 'var(--border)', color: 'var(--muted-foreground)' }
                    : { borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  {sendingCode && verificationMethod === 'email' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      Send to Email
                    </span>
                  )}
                </button>
                {userContact.phone && (
                  <button
                    type="button"
                    onClick={async () => { setVerificationMethod('sms'); await send2FACode('sms'); }}
                    disabled={sendingCode}
                    className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold border transition-all disabled:opacity-50"
                    style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                  >
                    {sendingCode && verificationMethod === 'sms' ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        Send to Phone
                      </span>
                    )}
                  </button>
                )}
              </div>
              <div className="text-xs text-[var(--muted-foreground)] space-y-0.5">
                <p>📧 Email: {userContact.email}</p>
                {userContact.phone && <p>📱 Phone: {userContact.phone}</p>}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-medium"
              style={{ backgroundColor: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" className="flex-shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
              <span className="text-emerald-700">
                Code sent successfully! Enter the 6-digit code below to continue.
              </span>
            </div>
          )}

          <AuthField label="Verification Code" htmlFor="code">
            <input
              id="code"
              name="code"
              type="text"
              autoComplete="one-time-code"
              required
              maxLength="6"
              placeholder="000000"
              className={`${AUTH_INPUT} text-center text-xl tracking-widest`}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
            />
          </AuthField>

          <div className="text-center">
            <button
              type="button"
              onClick={resend2FA}
              disabled={resendTimer > 0 || sendingCode}
              className="text-xs font-semibold transition-colors disabled:text-[var(--muted-foreground)] disabled:cursor-not-allowed"
              style={{ color: resendTimer > 0 || sendingCode ? undefined : 'var(--accent)' }}
            >
              {sendingCode ? 'Sending...' : resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend verification code'}
            </button>
          </div>

          <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Verifying…" disabled={verificationCode.length !== 6}>
            Verify Code
          </AuthPrimaryButton>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setShow2FA(false);
                setVerificationCode('');
                setError('');
                setCodeSent(false);
                setFormData({ email: '', password: '' });
                setTempToken('');
                setSendingCode(false);
                setLoading(false);
              }}
              className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
            >
              ← Back to login
            </button>
          </div>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
          {agency} Agent Portal
        </h2>
        <p className="text-sm text-[var(--muted-foreground)] mt-1">
          {isLogin ? 'Sign in to access your dashboard' : 'Apply to join our agency'}
        </p>
      </div>

      {error && typeof error === 'object' && error.type ? (
        <SecurityLockoutMessage
          error={error}
          onRetry={() => { setError(''); setLoading(false); }}
          onForgotPassword={() => navigate(`/${agencyCode.toLowerCase()}/password-reset`)}
        />
      ) : (
        isLogin ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <AuthField label="Email Address" htmlFor="email">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@agency.com"
                className={AUTH_INPUT}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </AuthField>

            <AuthField label="Password" htmlFor="password"
              hint={
                <button type="button" onClick={() => navigate(`/${agencyCode.toLowerCase()}/forgot-password`)}
                  className="text-xs font-semibold hover:underline transition-colors" style={{ color: 'var(--accent)' }}>
                  Forgot password?
                </button>
              }>
              <AuthPasswordInput
                id="password"
                name="password"
                autoComplete="current-password"
                required
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
              />
            </AuthField>

            <p className="text-xs text-[var(--muted-foreground)] text-center">
              Two-factor authentication is enabled for your security
            </p>

            <AuthErrorBanner>{typeof error === 'string' ? error : null}</AuthErrorBanner>

            <AuthPrimaryButton type="submit" loading={loading} loadingLabel="Signing in…">
              Sign In
            </AuthPrimaryButton>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <p className="text-sm text-[var(--muted-foreground)]">
              To apply to join {agency}, please click the button below to access our application form.
            </p>
            <AuthPrimaryButton type="button" onClick={() => { window.location.href = `/${agencyCode.toLowerCase()}/register`; }}>
              Apply to Join {agency}
            </AuthPrimaryButton>
          </div>
        )
      )}

      <div className="relative flex items-center gap-3 py-1 mt-6">
        <div className="flex-1 h-px bg-[var(--border)]" />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--muted-foreground)]">
          {isLogin ? `New to ${agency}?` : 'Already an agent?'}
        </span>
        <div className="flex-1 h-px bg-[var(--border)]" />
      </div>

      <button
        type="button"
        onClick={() => { setIsLogin(!isLogin); setError(''); }}
        className="w-full mt-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--muted)] transition-all"
      >
        {isLogin ? `Apply to join ${agency}` : 'Sign in instead'}
      </button>

      <div className="text-center mt-6">
        <a href={`/${agencyCode.toLowerCase()}/admin`} className="text-xs font-semibold hover:underline transition-colors" style={{ color: 'var(--accent)' }}>
          Agency Admin Login →
        </a>
      </div>
    </AuthShell>
  );
};

export default AgencyLoginPage;
